<template>
  <UnsubscribeViaSmsModal v-if="isUnsubscribeViaSmsModalOpen" />

  <UnsubscribeViaWebModal
    v-else-if="isUnsubscribeViaWebModalOpen"
    @unsubscribe:viaWeb="handleUnsubscribeViaWeb"
  />

  <UnsubscribeSuccessModal
    v-else-if="isUnsubscribeSuccessModalOpen"
    @resubscribe:viaSuccessUnsub="handleResubscribeViaSuccessUnsub"
  />

  <ResubscribeViaSmsModal v-else-if="isResubscribeViaSmsModalOpen" />

  <ResubscribeViaPinModal
    v-else-if="isResubscribeViaPinModalOpen"
    @resubscribe:viaPin="handleResubscribeViaPin"
  />

  <ResubscribeSuccessModal
    v-else-if="isResubscribeSuccessModalOpen"
    @proceed:successResubscribe="handleProceedFromSuccessResubscribe"
  />

  <ResubscribeErrorModal v-else-if="isResubscribeErrorModalOpen" />
</template>

<script setup>
import { onMounted } from 'vue';
import UnsubscribeViaSmsModal from '@/components/subscription/modal/UnsubscribeViaSmsModal.vue';
import UnsubscribeViaWebModal from '@/components/subscription/modal/UnsubscribeViaWebModal.vue';
import UnsubscribeSuccessModal from '@/components/subscription/modal/UnsubscribeSuccessModal.vue';
import ResubscribeViaSmsModal from '@/components/subscription/modal/ResubscribeViaSmsModal.vue';
import ResubscribeViaPinModal from '@/components/subscription/modal/ResubscribeViaPinModal.vue';
import ResubscribeErrorModal from '@/components/subscription/modal/ResubscribeErrorModal.vue';
import ResubscribeSuccessModal from '@/components/subscription/modal/ResubscribeSuccessModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';

const emits = defineEmits(['unsubscribe:resolved']);

const props = defineProps({
  lazy: {
    type: Boolean,
    default: false,
  },
});

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();

const { redirectToHomePage } = usePageNavigationHook();
const [isUnsubscribeViaSmsModalOpen, openUnsubscribeViaSmsModal] = useModalHook();
const [isUnsubscribeViaWebModalOpen, openUnsubscribeViaWebModal, closeUnsubscribeViaWebModal] = useModalHook();
const [isUnsubscribeSuccessModalOpen, openUnsubscribeSuccessModal, closeUnsubscribeSuccessModal] = useModalHook();
const [isResubscribeViaSmsModalOpen, openResubscribeViaSmsModal] = useModalHook();
const [isResubscribeViaPinModalOpen, openResubscribeViaPinModal, closeResubscribeViaPinModal] = useModalHook();
const [isResubscribeSuccessModalOpen, openResubscribeSuccessModal, closeResubscribeSuccessModal] = useModalHook();
const [isResubscribeErrorModalOpen, openResubscribeErrorModal] = useModalHook();

const handleUnsubscribeViaWeb = async (result) => {
  await closeUnsubscribeViaWebModal({ immediate: true });

  if (result.status === 'success' && !authStore.user.subscribed) {
    if (appSettingsStore.isResubscribeViaSmsSupported) {
      openResubscribeViaSmsModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
    } else {
      openUnsubscribeSuccessModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
    }
  } else {
    emits('unsubscribe:resolved');
  }
};

const handleResubscribeViaSuccessUnsub = async (result) => {
  await closeUnsubscribeSuccessModal({ immediate: true });

  if (result.status === 'success') {
    openResubscribeSuccessModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
  } else if (result.status === 'pinRequired') {
    openResubscribeViaPinModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
  } else {
    openResubscribeErrorModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
  }
};

const handleResubscribeViaPin = async (result) => {
  await closeResubscribeViaPinModal({ immediate: true });

  if (result.status === 'success') {
    openResubscribeSuccessModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
  } else {
    openResubscribeErrorModal({ showImmediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });
  }
};

const handleProceedFromSuccessResubscribe = async () => {
  await closeResubscribeSuccessModal({ immediate: true, afterHideCallback: () => emits('unsubscribe:resolved') });

  redirectToHomePage();
};

const unsubscribeUser = () => {
  if (appSettingsStore.isUnsubscribeViaSmsSupported) {
    openUnsubscribeViaSmsModal({ afterHideCallback: () => emits('unsubscribe:resolved') });
  } else {
    openUnsubscribeViaWebModal({ afterHideCallback: () => emits('unsubscribe:resolved') });
  }
};

onMounted(() => {
  if (!props.lazy) {
    unsubscribeUser();
  }
});

defineExpose({
  unsubscribeUser,
});
</script>
